import React, { useMemo, useState, useEffect } from "react";
// outsource
import { Layout, Col, Row, Modal, message } from "antd";
import * as XLSX from "xlsx";
// redux
import { useDispatch, useSelector } from "react-redux";
import {
  snapPayrateSettings,
  unSnapPayrateSettings,
  savePayrateSetting,
} from "../../store/payrate_setting/payrate_setting.action";
import {
  payrateSettingsSelector,
  payrateSettingsLoadingSelector,
} from "../../store/payrate_setting/payrate_setting.selector";
import { snapAllStaffs, UnSnapAllStaff } from "../../store/staff/staff.action";
import { allStaffsSelector } from "../../store/staff/staff.selector";
import { snapAllUsers, unSnapUsers } from "../../store/user/user.action";
import { usersSelector } from "../../store/user/user.selector";
import { calculatePayoutDailyData } from "../../store/payout_daily/payout_daily.action";
// components
import PayrateBonusToolbar from "./component/payrate_bonus_toolbar";
import PayrateBonusTable from "./component/payrate_bonus_table";
import LayoutBreadCrumb from "../00_Nav/layout/layout_bread_crumb";
import LayoutFooter from "../00_Nav/layout/layout_footer";
// dialogs
import PayrateBonusEditDialog from "./dialog/payrate_bonus_edit_dialog";
import PayrateBonusImportDialog from "./dialog/payrate_bonus_import_dialog";
// functions
import { formatMoney, formatTimestamp } from "./functions/payrate_bonus_format";
// style
import "./style.css";
import { URL } from "../00_Nav/url";

const { Content } = Layout;

const PayrateBonusScreen = () => {
  // -------------------------- VAR --------------------------
  const breadCrumb = [
    { title: "Home", url: URL.home },
    { title: "Payout", url: URL.payout },
    { title: "Payrate & Bonus", url: URL.payrateBonus },
  ];

  // -------------------------- STATE --------------------------
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editingStaff, setEditingStaff] = useState(null);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // -------------------------- REDUX --------------------------
  const dispatch = useDispatch();
  const allStaffs = useSelector(allStaffsSelector);
  const users = useSelector(usersSelector);
  const payrateSettings = useSelector(payrateSettingsSelector);
  const loading = useSelector(payrateSettingsLoadingSelector);

  // -------------------------- EFFECT --------------------------
  useEffect(() => {
    dispatch(snapAllStaffs());
    dispatch(snapAllUsers());
    dispatch(snapPayrateSettings());
    return () => {
      dispatch(UnSnapAllStaff());
      dispatch(unSnapUsers());
      dispatch(unSnapPayrateSettings());
    };
  }, [dispatch]);

  // -------------------------- FUNCTION --------------------------
  const data = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const staff of allStaffs || []) {
      // deduplicate by user UID across branches
      const uid = staff.staff;
      const key = uid || staff.id;
      if (seen.has(key)) continue;
      seen.add(key);

      const user = (users || []).find((u) => u.id === uid);
      const setting = payrateSettings?.[uid] || payrateSettings?.[staff.id] || {};

      // skip staff whose user account can't be resolved and has no saved setting —
      // this can happen when a staff document exists but the linked user was deleted
      // or created outside the normal flow; TODO: investigate and clean up orphaned staff docs
      if (!user && !setting.staffName) continue;

      const fullName = user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
        : setting.staffName || "";

      rows.push({
        id: uid || staff.id,
        fullName,
        category: setting.category || "",
        xeroPayrate: setting.xeroPayrate ?? "",
        xeroHours: setting.xeroHours ?? "",
        xeroPay: setting.xeroPay ?? "",
        includeXeroHours: setting.includeXeroHours ?? false,
        regularHrs: setting.regularHrs ?? "",
        nonXeroPayrate: setting.nonXeroPayrate ?? "",
        kiwiSaver: setting.kiwiSaver ?? "",
        bonusBase: setting.bonusBase ?? "",
        bonusStep: setting.bonusStep ?? "",
        bonusRate: setting.bonusRate ?? "",
        fuelSupport: setting.fuelSupport ?? "",
        lastUpdated: formatTimestamp(setting.updateAt),
      });
    }
    return rows;
  }, [allStaffs, users, payrateSettings]);

  const filteredData = useMemo(() => {
    const q = String(keyword || "")
      .trim()
      .toLowerCase();
    if (!q) return data;
    return data.filter((item) =>
      String(item.fullName || "")
        .toLowerCase()
        .includes(q),
    );
  }, [data, keyword]);


  const handleSave = (updatedStaff) => {
    dispatch(
      savePayrateSetting({
        staffId: updatedStaff.id,
        data: {
          staff: updatedStaff.id,
          staffName: updatedStaff.fullName,
          category: updatedStaff.category,
          xeroPayrate: updatedStaff.xeroPayrate,
          xeroHours: updatedStaff.xeroHours,
          xeroPay: updatedStaff.xeroPay,
          includeXeroHours: updatedStaff.includeXeroHours,
          regularHrs: updatedStaff.regularHrs,
          nonXeroPayrate: updatedStaff.nonXeroPayrate,
          kiwiSaver: updatedStaff.kiwiSaver,
          bonusBase: updatedStaff.bonusBase,
          bonusStep: updatedStaff.bonusStep,
          bonusRate: updatedStaff.bonusRate,
          fuelSupport: updatedStaff.fuelSupport,
        },
      }),
    );
    setEditingStaff(null);
  };

  const handleDownload = () => {
    const exportRows = filteredData.map((item) => ({
      "Full Name": item.fullName,
      Category: item.category,
      "XERO Payrate": item.xeroPayrate,
      "XERO Hours": item.xeroHours,
      "XERO Pay": item.xeroPay,
      "Include XERO Hours": item.includeXeroHours ? "Yes" : "No",
      "Regular Hours": item.regularHrs,
      "Non-XERO Payrate": item.nonXeroPayrate,
      "KiwiSaver": item.kiwiSaver,
      "Bonus Base": item.bonusBase,
      "Bonus Step": item.bonusStep,
      "Bonus Rate": item.bonusRate,
      "Fuel Support": item.fuelSupport,
      "Last Updated": item.lastUpdated,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [
      { wch: 24 },
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
      { wch: 12 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 22 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payrate Bonus");
    XLSX.writeFile(workbook, "Payrate_Bonus.xlsx");
  };

  const handleDownloadTemplate = () => {
    const templateRows = [
      {
        "Full Name": "",
        "Category": "",
        "XERO Payrate": "",
        "XERO Hours": "",
        "XERO Pay": "",
        "Include XERO Hours": "No",
        "Regular Hours": "",
        "Non-XERO Payrate": "",
        "KiwiSaver": "",
        "Bonus Base": "",
        "Bonus Step": "",
        "Bonus Rate": "",
        "Fuel Support": "",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateRows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = "Payrate_Bonus_Template.csv";
    link.click();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleRecalculate = () => {
    setRecalculating(true);
    dispatch(
      calculatePayoutDailyData((response) => {
        setRecalculating(false);
        if (response?.status === 200) {
          message.success("Recalculate successfully");
          return;
        }
        message.error(response?.data || "Recalculate failed");
      }),
    );
  };

  const handleImportCsv = () => {
    if (!importFile) {
      Modal.warning({ title: "No file selected", content: "Please select a CSV file to import." });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "string" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);

        // nameMap: fullName (lowercase) → user UID (data[i].id is already user UID)
        const nameMap = {};
        data.forEach((item) => {
          if (item.fullName) nameMap[item.fullName.trim().toLowerCase()] = item.id;
        });

        const toNum = (val) => (val !== undefined && val !== "" ? Number(val) : "");
        const toBool = (val) =>
          ["true", "yes", "1"].includes(
            String(val || "").trim().toLowerCase(),
          );

        let matched = 0;
        const unmatched = [];

        rows.forEach((row) => {
          const fullName = String(row["Full Name"] || "").trim();
          if (!fullName) return;
          const staffId = nameMap[fullName.toLowerCase()];
          if (!staffId) {
            unmatched.push(fullName);
            return;
          }
          dispatch(
            savePayrateSetting({
              staffId,
              data: {
                staff: staffId,
                staffName: fullName,
                category: String(row["Category"] || "").trim(),
                xeroPayrate: toNum(row["XERO Payrate"]),
                xeroHours: toNum(row["XERO Hours"]),
                xeroPay: toNum(row["XERO Pay"]),
                includeXeroHours: toBool(row["Include XERO Hours"]),
                regularHrs: toNum(row["Regular Hours"]),
                nonXeroPayrate: toNum(row["Non-XERO Payrate"]),
                kiwiSaver: toNum(row["KiwiSaver"]),
                bonusBase: toNum(row["Bonus Base"]),
                bonusStep: toNum(row["Bonus Step"]),
                bonusRate: toNum(row["Bonus Rate"]),
                fuelSupport: toNum(row["Fuel Support"]),
              },
            }),
          );
          matched++;
        });

        setImportFile(null);
        setOpenImportModal(false);

        if (unmatched.length > 0) {
          Modal.warning({
            title: `Imported ${matched} record(s)`,
            content: (
              <div>
                <div>List of non-existing staff(s):</div>
                {unmatched.map((staffName, index) => (
                  <div key={`${staffName}-${index}`}>{`${index + 1}. ${staffName}`}</div>
                ))}
              </div>
            ),
          });
        } else {
          Modal.success({ title: `Imported ${matched} record(s) successfully` });
        }
      } catch (err) {
        Modal.error({ title: "Import failed", content: err.message });
      }
    };
    reader.readAsText(importFile);
  };

  // -------------------------- RENDER --------------------------
  const renderBody = () => {
    return (
      <Row>
        <Col xl={24} lg={24} md={24} xs={24}>
          <div className="payrate-bonus-page">
            <PayrateBonusToolbar
              keyword={keyword}
              setKeyword={setKeyword}
              setPage={setPage}
              onDownload={handleDownload}
              onOpenImport={() => setOpenImportModal(true)}
              onRecalculate={handleRecalculate}
              recalculating={recalculating}
            />
            <PayrateBonusTable
              filteredData={filteredData}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={(p, ps) => { setPage(p); setPageSize(ps); }}
              formatMoney={formatMoney}
              onEdit={setEditingStaff}
            />
          </div>
        </Col>
      </Row>
    );
  };

  // -------------------------- MAIN --------------------------
  return (
    <Layout className="site-layout full">
      <Content className="content">
        <div style={{ paddingLeft: 16 }}>
          <LayoutBreadCrumb data={breadCrumb} />
        </div>
        {renderBody()}
      </Content>
      <PayrateBonusEditDialog
        editingStaff={editingStaff}
        onClose={() => setEditingStaff(null)}
        onSave={handleSave}
      />
      <PayrateBonusImportDialog
        open={openImportModal}
        file={importFile}
        onClose={() => {
          setImportFile(null);
          setOpenImportModal(false);
        }}
        onSelectFile={setImportFile}
        onDownloadTemplate={handleDownloadTemplate}
        onImport={handleImportCsv}
      />
      <LayoutFooter />
    </Layout>
  );
};

export default PayrateBonusScreen;
